import { Component, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RatesFilterStateService } from '../../state/rates-filter-state.service';
import { POPOVER_CONTEXT, PickerComponent } from './picker.types';
import { pickerRegistry } from './picker-registry';

/**
 * RILA/VA filter block — MinPremiumRangePickerComponent (2026-06-05).
 *
 * Two number inputs (Min + Max) writing `minPremiumMin` / `minPremiumMax`
 * on the active slot. Live-apply on input change, matching the established
 * pattern (NumericPicker, RangePicker). The registry entry's `field` is
 * `minPremiumMin` for type-system happiness; this picker reads/writes
 * both fields directly via the filter-state service.
 *
 * RILA/VA-only — hidden on FA/IA via DIM_TAB_APPLICABILITY.
 */
@Component({
  selector: 'app-min-premium-range-picker',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './min-premium-range-picker.component.html',
  styleUrl: './min-premium-range-picker.component.scss',
})
export class MinPremiumRangePickerComponent implements PickerComponent {
  protected readonly ctx = inject(POPOVER_CONTEXT);
  protected readonly filterState = inject(RatesFilterStateService);

  protected readonly minValue = computed<number | null>(() => {
    this.filterState.version();
    return this.filterState.activeSlot().minPremiumMin;
  });

  protected readonly maxValue = computed<number | null>(() => {
    this.filterState.version();
    return this.filterState.activeSlot().minPremiumMax;
  });

  onMinInput(value: string): void {
    const n = value === '' ? null : Number(value);
    this.filterState.setSlotField('minPremiumMin', Number.isFinite(n as number) ? n : null);
    this.filterState.requestApplyFilters();
  }

  onMaxInput(value: string): void {
    const n = value === '' ? null : Number(value);
    this.filterState.setSlotField('minPremiumMax', Number.isFinite(n as number) ? n : null);
    this.filterState.requestApplyFilters();
  }

  clearAll(): void {
    this.filterState.clearAppliedDimension(this.ctx.dimensionKey);
    this.filterState.requestApplyFilters();
  }
}

pickerRegistry['min-premium'] = {
  component: MinPremiumRangePickerComponent,
  label: 'Min Premium',
  // Type-system tag — the picker reads/writes both minPremiumMin AND
  // minPremiumMax via the service. The registry only carries one field
  // per entry; clearAppliedDimension('min-premium') clears both.
  field: 'minPremiumMin',
  group: 'Features',
};
